import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Eye, Clock, MapPin, Filter, ChevronRight, Camera, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Layout from "@/components/layout/Layout";
import PageHero from "@/components/shared/PageHero";
import { AdBanner } from "@/components/shared/AdPlaceholder";
import axios from "axios";
import { API } from "@/App";

const categoryLabels = {
  general: { label: "Historia", color: "bg-blue-100 text-blue-800" },
  "antes-despues": { label: "Antes / Después", color: "bg-purple-100 text-purple-800" },
  tradicion: { label: "Tradición", color: "bg-orange-100 text-orange-800" },
  personaje: { label: "Personaje", color: "bg-green-100 text-green-800" },
  oficio: { label: "Oficio", color: "bg-yellow-100 text-yellow-800" },
};

function MemoryCard({ memory }) {
  const placeholderImage = "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80";
  const catInfo = categoryLabels[memory.category] || categoryLabels.general;

  // Helper to resolve image URLs
  const getImageUrl = (url) => {
    if (!url) return placeholderImage;
    if (url.startsWith('http')) return url;
    // For relative URLs starting with /uploads, convert to /api/uploads for proper routing
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    if (url.startsWith('/uploads/')) {
      return `${backendUrl}/api${url}`;
    }
    return `${backendUrl}${url}`;
  };

  const featuredImage = getImageUrl(memory.featured_image_url);
  const beforeImage = getImageUrl(memory.before_image_url);
  const afterImage = getImageUrl(memory.after_image_url);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="group"
    >
      <Link
        to={`/memorias/${memory.slug}`}
        className="block bg-white rounded-3xl overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-500"
        data-testid={`memory-card-${memory.slug}`}
      >
        {/* Image */}
        <div className="relative aspect-[4/3] overflow-hidden">
          {memory.before_image_url && memory.after_image_url ? (
            <div className="relative w-full h-full">
              <img
                src={beforeImage}
                alt="Antes"
                className="absolute inset-0 w-1/2 h-full object-cover"
              />
              <img
                src={afterImage}
                alt="Después"
                className="absolute right-0 top-0 w-1/2 h-full object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-1 h-full bg-white shadow-lg" />
              </div>
              <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 text-white text-xs rounded">
                Antes
              </div>
              <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 text-white text-xs rounded">
                Después
              </div>
            </div>
          ) : (
            <img
              src={featuredImage}
              alt={memory.title}
              className="w-full h-full object-cover img-zoom"
            />
          )}
          <div className="absolute top-4 left-4">
            <Badge className={`${catInfo.color} border-0`}>
              {catInfo.label}
            </Badge>
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          <h3 className="font-heading text-lg font-bold text-brand-text line-clamp-2 group-hover:text-primary transition-colors">
            {memory.title}
          </h3>
          {memory.year_approx && (
            <p className="text-brand-textMuted text-sm mt-1 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {memory.year_approx}
            </p>
          )}
          {memory.excerpt && (
            <p className="text-brand-textSecondary text-sm mt-2 line-clamp-2">
              {memory.excerpt}
            </p>
          )}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-4 text-sm text-brand-textMuted">
              <span className="flex items-center gap-1">
                <Heart className="h-4 w-4" />
                {memory.vote_count || 0}
              </span>
              <span className="flex items-center gap-1">
                <Eye className="h-4 w-4" />
                {memory.view_count || 0}
              </span>
            </div>
            {memory.municipio && (
              <span className="flex items-center gap-1 text-sm text-brand-textMuted">
                <MapPin className="h-3 w-3" />
                {memory.municipio}
              </span>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default function MemoriasPage() {
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [location, setLocation] = useState("all");

  useEffect(() => {
    fetchMemories();
  }, [category, location]);

  const fetchMemories = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category !== "all") params.append("category", category);
      if (location !== "all") params.append("location_scope", location);
      params.append("limit", "20");
      
      const response = await axios.get(`${API}/public/memories?${params}`);
      setMemories(response.data.items);
    } catch (error) {
      console.error("Error fetching memories:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* Hero */}
      <PageHero
        section="memorias"
        title="Memorias de Nuestra Tierra"
        subtitle="Historias, tradiciones y recuerdos que mantienen viva la identidad de Choluteca y Honduras."
        badge="Historia Local"
        badgeIcon={BookOpen}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8"
        >
          <Link to="/compartir">
            <Button
              size="lg"
              className="bg-white text-primary hover:bg-white/90 rounded-full px-8"
              data-testid="share-memory-btn"
            >
              <Camera className="mr-2 h-5 w-5" />
              Compartir tu memoria
            </Button>
          </Link>
        </motion.div>
      </PageHero>

      {/* Filters */}
      <section className="bg-white border-b border-gray-100 sticky top-16 md:top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-brand-textSecondary">
              <Filter className="h-5 w-5" />
              Filtrar por:
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-48 rounded-full" data-testid="category-filter">
                <SelectValue placeholder="Categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                <SelectItem value="general">Historias</SelectItem>
                <SelectItem value="antes-despues">Antes / Después</SelectItem>
                <SelectItem value="tradicion">Tradiciones</SelectItem>
                <SelectItem value="personaje">Personajes</SelectItem>
                <SelectItem value="oficio">Oficios</SelectItem>
              </SelectContent>
            </Select>
            <Select value={location} onValueChange={setLocation}>
              <SelectTrigger className="w-40 rounded-full" data-testid="location-filter">
                <SelectValue placeholder="Ubicación" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="choluteca">Choluteca</SelectItem>
                <SelectItem value="honduras">Honduras</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-12 md:py-16" data-testid="memorias-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-gray-100 rounded-3xl h-[400px] animate-pulse" />
              ))}
            </div>
          ) : memories.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {memories.map((memory, idx) => (
                <MemoryCard key={memory.memory_id} memory={memory} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-3xl">
              <Camera className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">
                Aún no hay memorias publicadas
              </h3>
              <p className="text-gray-500 mb-6">
                ¡Sé el primero en compartir una historia!
              </p>
              <Link to="/compartir">
                <Button className="rounded-full">
                  Compartir memoria
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-primary to-secondary" data-testid="memorias-cta">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            ¿Tienes fotos o historias antiguas?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Comparte tus recuerdos y ayuda a preservar la historia de nuestra comunidad.
            Fotos antiguas, relatos familiares, tradiciones perdidas...
          </p>
          <Link to="/compartir">
            <Button
              size="lg"
              className="bg-white text-primary hover:bg-white/90 rounded-full px-8"
            >
              <Camera className="mr-2 h-5 w-5" />
              Enviar mi memoria
            </Button>
          </Link>
        </div>
      </section>

      {/* Ad Banner */}
      <section className="py-8 bg-gray-50">
        <AdBanner />
      </section>
    </Layout>
  );
}
